import { apiClient } from "@api/client";
import { request } from "@api/request";

import { words } from "./traceFormat";
import type {
  JevCallDetail,
  JevCallPage,
  JevTickPage,
  JevTickSummary,
} from "./types";

const JEV_CALL_PAGE_SIZE = 25;
const JEV_TICK_PAGE_SIZE = 15;

/** brian `room_perceive._Calls` question names; change both together. */
export const JEV_QUESTIONS: { value: string; label: string }[] = [
  { value: "board", label: "Board" },
  { value: "transition", label: "Transition (T3)" },
  { value: "quad_count", label: "Count per quarter" },
  { value: "leaf_box", label: "Box one body" },
  { value: "split_box", label: "Split a crowded crop" },
  { value: "same_body", label: "Same body?" },
  { value: "head_pose", label: "Head pose" },
  { value: "roster_diff", label: "Roster check" },
  { value: "staff_naming", label: "Staff naming" },
  { value: "visit", label: "Visit" },
];

const QUESTION_LABELS = new Map(JEV_QUESTIONS.map((q) => [q.value, q.label]));

export const questionLabel = (question: string): string =>
  QUESTION_LABELS.get(question) ?? words(question);

export interface JevCallFilters {
  roomUuid: string;
  question: string;
  failed: boolean;
  /** brian `room_locate_packet.LOCATE_QUESTIONS`: listed only when true. */
  boxFinding: boolean;
  search: string;
}

export const fetchJevCallPage = (
  cursor: string | undefined,
  filters: JevCallFilters,
  signal: AbortSignal,
): Promise<JevCallPage> =>
  request(() =>
    apiClient.GET("/perception/jev-calls", {
      params: {
        query: {
          cursor,
          limit: JEV_CALL_PAGE_SIZE,
          room_uuid: filters.roomUuid || undefined,
          question: filters.question || undefined,
          failed: filters.failed || undefined,
          box_finding: filters.boxFinding ? undefined : false,
          search: filters.search || undefined,
        },
      },
      signal,
    }),
  );

export const fetchJevCallDetail = (
  callUuid: string,
  signal: AbortSignal,
): Promise<JevCallDetail> =>
  request(() =>
    apiClient.GET("/perception/jev-calls/{call_uuid}", {
      params: { path: { call_uuid: callUuid } },
      signal,
    }),
  );

export const fetchJevTickPage = (
  cursor: string | undefined,
  roomUuid: string,
  signal: AbortSignal,
): Promise<JevTickPage> =>
  request(() =>
    apiClient.GET("/perception/jev-ticks", {
      params: {
        query: {
          cursor,
          limit: JEV_TICK_PAGE_SIZE,
          room_uuid: roomUuid || undefined,
        },
      },
      signal,
    }),
  );

export const fetchJevTick = (
  roomUuid: string,
  tickedAt: string,
  signal: AbortSignal,
): Promise<JevTickSummary> =>
  request(() =>
    apiClient.GET("/perception/jev-ticks/{room_uuid}", {
      params: { path: { room_uuid: roomUuid }, query: { ticked_at: tickedAt } },
      signal,
    }),
  );
